// Pass 5.2 — /status page's filter/sort/search preferences.
// Persisted across sessions in localStorage so the operator
// doesn't have to re-type their filter every page load.
//
// Hardening: a malformed / missing / corrupt prefs blob NEVER
// crashes the page — every parse path falls back to DEFAULTS.
// Fresh users (no localStorage entry) get DEFAULTS too. The
// only thing that can change defaults is the user explicitly
// interacting with the toolbar.

import { useCallback, useEffect, useState } from "react";

export type StatusFilterKind = "all" | "active" | "needs_reauth" | "disabled";
export type StatusSortKind =
  | "severity"      // status-broken-first (default)
  | "name"          // A-Z by team_name
  | "polled"        // stalest poll first
  | "last_message"; // most recent message first

export interface StatusListPrefs {
  search: string;
  status: StatusFilterKind;
  sort: StatusSortKind;
}

export const STATUS_LIST_PREFS_STORAGE_KEY = "slack-archive:status-list-prefs";

export const DEFAULT_STATUS_LIST_PREFS: StatusListPrefs = {
  search: "",
  status: "all",
  sort: "severity",
};

const VALID_STATUS: ReadonlySet<StatusFilterKind> = new Set([
  "all",
  "active",
  "needs_reauth",
  "disabled",
]);
const VALID_SORT: ReadonlySet<StatusSortKind> = new Set([
  "severity",
  "name",
  "polled",
  "last_message",
]);

function readFromStorage(): StatusListPrefs {
  // SSR / test environments without localStorage just get defaults.
  if (typeof window === "undefined" || !window.localStorage) {
    return { ...DEFAULT_STATUS_LIST_PREFS };
  }
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STATUS_LIST_PREFS_STORAGE_KEY);
  } catch {
    // Quota errors / disabled storage → defaults.
    return { ...DEFAULT_STATUS_LIST_PREFS };
  }
  if (!raw) return { ...DEFAULT_STATUS_LIST_PREFS };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_STATUS_LIST_PREFS };
  }
  if (!parsed || typeof parsed !== "object") {
    return { ...DEFAULT_STATUS_LIST_PREFS };
  }
  const obj = parsed as Record<string, unknown>;
  const search = typeof obj.search === "string" ? obj.search : DEFAULT_STATUS_LIST_PREFS.search;
  const status =
    typeof obj.status === "string" && VALID_STATUS.has(obj.status as StatusFilterKind)
      ? (obj.status as StatusFilterKind)
      : DEFAULT_STATUS_LIST_PREFS.status;
  const sort =
    typeof obj.sort === "string" && VALID_SORT.has(obj.sort as StatusSortKind)
      ? (obj.sort as StatusSortKind)
      : DEFAULT_STATUS_LIST_PREFS.sort;
  return { search, status, sort };
}

function writeToStorage(prefs: StatusListPrefs): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      STATUS_LIST_PREFS_STORAGE_KEY,
      JSON.stringify(prefs),
    );
  } catch {
    // Quota / disabled storage — swallow. The in-memory state
    // still reflects what the user picked this session.
  }
}

/** React hook: hydrate from localStorage on mount, persist on
 *  every change. Returns [prefs, setPrefs] symmetric with
 *  useState. */
export function useStatusListPrefs(): [
  StatusListPrefs,
  (next: StatusListPrefs | ((prev: StatusListPrefs) => StatusListPrefs)) => void,
] {
  const [prefs, setPrefsState] = useState<StatusListPrefs>(() => readFromStorage());

  // Persist on every change AFTER mount. The initial read already
  // brought the stored values in; this effect is for subsequent
  // updates the user triggers via the toolbar.
  useEffect(() => {
    writeToStorage(prefs);
  }, [prefs]);

  const setPrefs = useCallback<typeof setPrefsState>((next) => {
    setPrefsState(next);
  }, []);

  return [prefs, setPrefs];
}
